import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import type { ChatSession } from '@nxsys/types/models/AiChat';
import type { AiAdminSessionDetail } from '@nxsys/store/slices/superAdmin/types';

export const getSessions = async (): Promise<ChatSession[]> => {
  const { data } = await jwtAxios.get(endPoints.ai.sessions);
  return data;
};

export const getSession = async (sessionId: string): Promise<ChatSession> => {
  const { data } = await jwtAxios.get(endPoints.ai.session(sessionId));
  return data;
};

export const deleteSession = async (sessionId: string) => {
  const { data } = await jwtAxios.delete(endPoints.ai.session(sessionId));
  return data;
};

export type ChatConfig = {
  title: string;
  placeholder: string;
  greeting: string;
  suggestions: { label: string; prompt: string }[];
};

export const getChatConfig = async (): Promise<ChatConfig> => {
  const { data } = await jwtAxios.get(endPoints.ai.chatConfig);
  return data;
};

export const submitSessionFeedback = (
  sessionId: string,
  data: { rating: 'up' | 'down'; comment?: string },
) => jwtAxios.put(endPoints.ai.sessionFeedback(sessionId), data);

export type DailyGreeting = {
  greeting: string;
  isNewUser: boolean;
  suggestions: { label: string; prompt: string; promptTemplate?: string }[];
};

export const getDailyGreeting = async (): Promise<DailyGreeting> => {
  const { data } = await jwtAxios.get(endPoints.ai.dailyGreeting);
  return data;
};

export type CreateTicketPayload = {
  subject: string;
  description: string;
  sessionId?: string;
  attachments?: string[];
  // When set, the backend adds a comment to this existing ticket instead of creating a new one.
  appendToTicketNumber?: string;
};

/**
 * Nxsys only takes queries from its own customers (NX-5683). A refusal carries a
 * reason naming who the query has to go to instead, which is safe to show; other
 * failures carry internal text and must not reach the user.
 */
export class SupportTicketRefusedError extends Error {}

const asRefusal = (body: any) => {
  const reason = body?.message;
  return new SupportTicketRefusedError(
    (Array.isArray(reason) ? reason.join(', ') : String(reason || '')) ||
      'This query cannot be raised with Nxsys.',
  );
};

export const createSupportTicket = async (
  payload: CreateTicketPayload,
): Promise<{ ticketNumber?: string; webUrl?: string }> => {
  try {
    const res = await jwtAxios.post(endPoints.ai.tickets, payload);
    // The shared axios instance resolves a 403-with-body as success, so a refusal
    // can arrive here instead of being thrown. Key off the status, not the body
    // shape — a 403 from a proxy carries no NestJS envelope.
    if (res.status >= 400) throw asRefusal(res.data);
    return res.data;
  } catch (error: any) {
    if (error instanceof SupportTicketRefusedError) throw error;
    // ...and it rejects normally when that interceptor is not in play.
    if (error?.response?.status === 403) throw asRefusal(error.response.data);
    throw error;
  }
};

// --- Super-admin cross-tenant AI chat management (@Roles('super_admin')) ---

/** Fetch one session with its full raw conversation + per-message metadata. */
export const adminGetSession = async (
  sessionId: string,
): Promise<AiAdminSessionDetail> => {
  const { data } = await jwtAxios.get(endPoints.ai.admin.session(sessionId));
  return data;
};

/** Download filtered sessions as CSV (returns raw CSV text). */
export const adminExportSessions = async (
  params: Record<string, unknown>,
): Promise<string> => {
  const { data } = await jwtAxios.get(endPoints.ai.admin.export, {
    params,
    responseType: 'text',
  });
  return data as string;
};
